import { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { OrgCard } from '../OrgCard'
import { GetPlanOrganizationsForAnnualPlan } from '../../../../services/AnnualPlanService'
import { type OrganizationOption } from '../../../../models/requestObjects/CommonObjects'
import { useNavigate } from 'react-router-dom'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../../Routes'
import { AnnualPlanTabs } from '../PlanningDetails'

interface Props {
  annualPlanId: number
  rcnId: number
  implementationPhase?: boolean
}

export const PlanOrganizationSelection = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const [planOrgs, setPlanOrgs] = useState<OrganizationOption[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const getPlanOrgs = async (): Promise<void> => {
      try {
        setIsLoading(true)

        const { success, response: planOrgs } = await GetPlanOrganizationsForAnnualPlan(props.annualPlanId)

        if (success) {
          setPlanOrgs(planOrgs)
        } else {
          setError('Failed to fetch Plan Organizations for Annual Plan.')
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'An unknown error occured while fetching Plan Organizations for Annual Plan.')
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    void getPlanOrgs()
  }, [props.annualPlanId])

  const handlePlanOrgSelected = (planOrgId: number): void => {
    const route = generatePath(
      props.implementationPhase === true
        ? AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION
        : AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION,
      {
        rcnId: props.rcnId.toString(),
        planId: props.annualPlanId.toString(),
        tab: String(AnnualPlanTabs.PriorityActivities),
        planOrgId: planOrgId.toString()
      }
    )
    nav(route)
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (error != null) {
    return <Typography variant='body1' textAlign="center">{error}</Typography>
  }

  return <Box data-testid="planOrgSelection" sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  }}
  >
    <Typography variant='h4'>ISDs</Typography>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '33px'
    }}
    >
      {planOrgs.map((planOrg, index) =>
        <OrgCard
          index={index}
          key={planOrg.id}
          name={planOrg.name}
          imageKey={planOrg.imageKey}
          buttonText='View Activities'
          onClick={() => { handlePlanOrgSelected(planOrg.id) }}
          dataTestId='planOrgCard'
          buttonDataTestId='viewActivitiesButton'
        />)}
    </Box>
  </Box>
}
